import { useMemo } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { OneEmptyState } from '@factorialco/f0-react/dist/experimental'
import { Crown, Money, People, Split } from '@factorialco/f0-react/icons/app'
import {
  CreateProcurementTypeWorkflowDocument,
  FindWorkflowDocument,
  ProcurementTypeItemFragment,
  RequestTypeDetailDocument,
  WorkflowContainerFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useBannerContext } from 'modules/layout/hooks/useBannerContext'
import { basicTaskApproveNodeToSchemaMapper } from 'modules/procurement/components/procurementTypes/Workflows/sidePanels/schemas/nodeToSchemaMappers/basicTask'
import { downloadDocumentNodeToSchemaMapper } from 'modules/procurement/components/procurementTypes/Workflows/sidePanels/schemas/nodeToSchemaMappers/downloadDocument'
import { manualApproveNodeToSchemaMapper } from 'modules/procurement/components/procurementTypes/Workflows/sidePanels/schemas/nodeToSchemaMappers/manualApprove'
import { VariablesConfig } from 'modules/workflows/components/Sidepanel/VariableSelectorTypes'
import { EmptyHandlerForm } from 'modules/workflows/components/Sidepanel/handlerForms/EmptyHandlerForm'
import { createApprovalFormSchema } from 'modules/workflows/components/Sidepanel/schemas/approvalFormSchema'
import { WorkflowContainer } from 'modules/workflows/components/WorkflowContainer'
import { mapWorkflowNodeFilesToNodes } from 'modules/workflows/lib/mapWorkflowNodeFilesToNodes'

import RoleSelector from './VariableSelectors/RoleSelector'
import TeamSelector from './VariableSelectors/TeamSelector'
import TeamSelectorMulti from './VariableSelectors/TeamSelectorMulti'
import {
  BasicTaskHandlerForm,
  DownloadDocumentHandlerForm,
  RequestDocumentHandlerForm,
  RequestReviewHandlerForm,
} from './sidePanels/handlers'
import { createBasicTaskHandlerSchema } from './sidePanels/schemas/basicTaskHandler'
import {
  type DownloadDocumentHandlerSchema,
  createDownloadDocumentHandlerSchema,
} from './sidePanels/schemas/downloadDocumentHandler'
import {
  type ManualApproveHandlerSchema,
  createManualApproveHandlerSchema,
} from './sidePanels/schemas/manualApproveHandler'
import { requestDocumentNodeToSchemaMapper } from './sidePanels/schemas/nodeToSchemaMappers/requestDocument'
import {
  type RequestDocumentHandlerSchema,
  createRequestDocumentHandlerSchema,
} from './sidePanels/schemas/requestDocumentHandler'
import { useWorkflowEntityResolution } from './useWorkflowEntityResolution'

interface props {
  requestType: ProcurementTypeItemFragment
  workflow: WorkflowContainerFragment | null
}

const WorkflowsTab = ({ requestType, workflow }: props) => {
  const { company, employee } = useCurrentSession()
  const { hasBanner } = useBannerContext()

  // Define variables config for WorkflowContainer
  const variablesConfig: VariablesConfig = useMemo(
    () => ({
      role_id: {
        type: 'entity',
        entity: 'role',
        label: i18n.t(
          'procurement.purchase_requests.settings.types.detail.tabs.workflows.variables.requester_role'
        ),
        icon: Crown,
        component: {
          single: RoleSelector,
        },
      },
      team_id: {
        type: 'entity',
        entity: 'team',
        label: i18n.t(
          'procurement.purchase_requests.settings.types.detail.tabs.workflows.variables.requester_team'
        ),
        icon: People,
        multiSelect: true,
        component: {
          single: TeamSelector,
          multi: TeamSelectorMulti,
        },
      },
      amount: {
        type: 'number',
        label: i18n.t(
          'procurement.purchase_requests.settings.types.detail.tabs.workflows.variables.purchase_request_amount'
        ),
        icon: Money,
        unit: requestType?.workflowCurrency ? requestType.workflowCurrency : undefined,
      },
    }),
    [requestType.workflowCurrency]
  )

  // Create workflow mutation
  const [createWorkflow, { loading: isCreating }] = useMutation(
    CreateProcurementTypeWorkflowDocument,
    {
      extractorFn: (x) => x.procurementMutations.createWorkflowProcurementType,
      refetchQueries: [RequestTypeDetailDocument, FindWorkflowDocument],
    }
  )

  const handleCreateWorkflow = async () => {
    if (!company?.id || !requestType.id) return

    try {
      const result = await createWorkflow({
        variables: {
          companyId: company.id,
          typeId: requestType.id,
          authorId: employee.id,
        },
      })

      const handled = handleResult(result, (errors) => {
        const errorMessages = Object.values(errors).join(', ')
        app.setFlash({
          type: 'wrong',
          message: i18n.t(
            'procurement.purchase_requests.settings.types.detail.tabs.workflows.error.create_failed',
            { errors: errorMessages }
          ),
          time: 3000,
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t(
            'procurement.purchase_requests.settings.types.detail.tabs.workflows.success.created'
          ),
          time: 3000,
        })
      }
    } catch (error) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t(
          'procurement.purchase_requests.settings.types.detail.tabs.workflows.error.create_failed',
          { errors: error }
        ),
        time: 3000,
      })
    }
  }

  if (workflow) {
    const heightOffset = hasBanner ? '264px' : '220px'

    return (
      <WorkflowContainer
        heightOffset={heightOffset}
        workflow={workflow}
        handlers={{
          auto_approve: {
            dropDownLabel: i18n.t('workflows.approval.types.auto_approve'),
            component: EmptyHandlerForm,
            initializer: () => ({}),
            schema: createApprovalFormSchema,
          },
          auto_reject: {
            dropDownLabel: i18n.t('workflows.approval.types.auto_reject'),
            component: EmptyHandlerForm,
            initializer: () => ({}),
            schema: createApprovalFormSchema,
          },
          request_review: {
            dropDownLabel: i18n.t('workflows.approval.types.request_review'),
            component: RequestReviewHandlerForm,
            beforeSave: (payload, handlerType) => {
              // Type guard for ManualApproveHandlerSchema payload
              const isManualApproveHandlerPayload = (
                value: unknown
              ): value is ManualApproveHandlerSchema['nodeData']['payload'] => {
                if (handlerType == 'request_review') {
                  return true
                }
                return false
              }

              if (!isManualApproveHandlerPayload(payload)) {
                throw new Error('Invalid payload for request_review handler')
              }

              return {
                ...payload,
                employee_segments: payload.employee_segments || [],
              }
            },
            schema: createManualApproveHandlerSchema,
            initializer: manualApproveNodeToSchemaMapper,
          },
          create_manual_task: {
            dropDownLabel: i18n.t('workflows.task.types.basic'),
            component: BasicTaskHandlerForm,
            initializer: basicTaskApproveNodeToSchemaMapper,
            schema: createBasicTaskHandlerSchema,
          },
          request_document: {
            dropDownLabel: i18n.t('workflows.task.types.request_document'),
            component: RequestDocumentHandlerForm,
            initializer: requestDocumentNodeToSchemaMapper,
            schema: createRequestDocumentHandlerSchema,
            beforeSave: (payload, handlerType) => {
              const isRequestDocumentPayload = (
                value: unknown
              ): value is RequestDocumentHandlerSchema['nodeData']['payload'] => {
                if (handlerType === 'request_document') {
                  return true
                }
                return false
              }

              if (!isRequestDocumentPayload(payload)) {
                throw new Error('Invalid payload for request_document handler')
              }

              // Filter out 'purchase_requester' from employee_ids
              // The assign_to_requester boolean already reflects whether it was selected
              const filteredEmployeeIds = (payload.employee_ids || []).filter(
                (id): id is number => typeof id === 'number'
              )

              return {
                ...payload,
                employee_ids: filteredEmployeeIds,
              }
            },
          },
          download_document: {
            dropDownLabel: i18n.t('workflows.task.types.download_document'),
            component: DownloadDocumentHandlerForm,
            initializer: downloadDocumentNodeToSchemaMapper,
            schema: createDownloadDocumentHandlerSchema,
            beforeSave: (payload, handlerType, context) => {
              const isDownloadDocumentPayload = (
                value: unknown
              ): value is DownloadDocumentHandlerSchema['nodeData']['payload'] => {
                if (handlerType === 'download_document') {
                  return true
                }
                return false
              }

              if (!isDownloadDocumentPayload(payload)) {
                throw new Error('Invalid payload for download_document handler')
              }

              if (!context) {
                throw new Error('Context is required for download_document handler')
              }

              // Filter out 'purchase_requester' from employee_ids
              // The assign_to_requester boolean already reflects whether it was selected
              const filteredEmployeeIds = (payload.employee_ids || []).filter(
                (id): id is number => typeof id === 'number'
              )

              // Map workflow node files to nodes and get files for this node
              const workflowNodeFiles = context.workflow.workflowNodeFiles || []
              const filesByNode = mapWorkflowNodeFilesToNodes(
                workflowNodeFiles,
                context.workflow.workflowNodes
              )
              const nodeFiles = filesByNode.get(context.nodeId) ?? []

              // Extract IDs from workflow node files
              const workflowNodeFilesIds = nodeFiles.map((file) => file.id)

              return {
                ...payload,
                employee_ids: filteredEmployeeIds,
                workflow_node_files_ids: workflowNodeFilesIds,
                // Remove files from payload as we're using workflow_node_files_ids instead
                files: undefined,
              }
            },
          },
        }}
        variables={variablesConfig}
        useEntityResolution={useWorkflowEntityResolution}
        trigger={{
          title: i18n.t(
            'procurement.purchase_requests.settings.types.detail.tabs.workflows.trigger.title'
          ),
          description: i18n.t(
            'procurement.purchase_requests.settings.types.detail.tabs.workflows.trigger.description'
          ),
        }}
        conditionsHeaderLabel={i18n.t('workflows.sidepanel.condition.paths.conditions_header')}
        dynamicInputsHeaderLabel={i18n.t(
          'workflows.sidepanel.condition.paths.dynamic_inputs_header'
        )}
      />
    )
  }

  // Show Add Button if no workflows exist
  return (
    <Box height="full" minHeight="s600" justifyContent="center" alignItems="center" gap="s24">
      <OneEmptyState
        actions={[
          {
            label: isCreating
              ? i18n.t(
                  'procurement.purchase_requests.settings.types.detail.tabs.workflows.action.creating'
                )
              : i18n.t(
                  'procurement.purchase_requests.settings.types.detail.tabs.workflows.action.add'
                ),
            onClick: handleCreateWorkflow,
            type: 'default',
            variant: 'outline',
            icon: Split,
          },
        ]}
        description={i18n.t(
          'procurement.purchase_requests.settings.types.detail.tabs.workflows.empty.description'
        )}
        emoji="🔀"
        title={i18n.t(
          'procurement.purchase_requests.settings.types.detail.tabs.workflows.empty.title'
        )}
      />
    </Box>
  )
}

export default WorkflowsTab
